import {useQuery} from '@apollo/client'
import React, {useEffect} from 'react'
import {GET_LEAD_LOGS} from '../../../gql/queries/leadsQuery'
import {toast} from 'react-toastify'
import {Accordion, Spinner} from 'react-bootstrap'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import moment from 'moment'

interface ILeadActivityProps {
  id: string
}

interface IResLogsLead {
  leadLogsByLeadId: {
    id: string
    oldValue: any
    newValue: any
    updatedAt: string
    user: {
      id: string
      firstName: string
      lastName: string
    } | null
    changes: string[]
  }[]
}

const toDict = (val: any) => {
  if (val == null) return {}
  if (typeof val === 'string') {
    try {
      return JSON.parse(val)
    } catch (e) {
      return val
    }
  }
  if (typeof val === 'object') return val
  return {}
}

const isPlainMessage = (oldVal: any, newVal: any) => {
  return (typeof oldVal === 'string' && oldVal.trim() !== '') || (typeof newVal === 'string' && newVal.trim() !== '')
}

const isNotificationLog = (newVal: any) =>
  typeof newVal === 'object' && newVal !== null && Array.isArray(newVal.notified)

const roleMessages = defineMessages({
  assigned_user: {id: 'leads.activity.role.assignedUser', defaultMessage: 'Assigned user'},
  supervisor: {id: 'leads.activity.role.supervisor', defaultMessage: 'Supervisor'},
  pipeline_reviewer: {id: 'leads.activity.role.reviewer', defaultMessage: 'Reviewer'},
})

const aM = defineMessages({
  empty: {id: 'leads.activity.empty', defaultMessage: 'There is no activity recorded at the moment'},
  changes: {id: 'leads.activity.changes', defaultMessage: 'Changes'},
  email: {id: 'common.field.email', defaultMessage: 'Email'},
  sent: {id: 'leads.activity.sent', defaultMessage: 'Sent'},
  queued: {id: 'leads.activity.queued', defaultMessage: 'Queued / not confirmed'},
  noRecipients: {id: 'leads.activity.noRecipients', defaultMessage: 'No recipients were notified.'},
  noStructured: {id: 'leads.activity.noStructured', defaultMessage: 'No structured changes to display.'},
  field: {id: 'leads.activity.field', defaultMessage: 'Field'},
  previousValue: {id: 'leads.activity.previousValue', defaultMessage: 'Previous Value'},
  newValue: {id: 'leads.activity.newValue', defaultMessage: 'New Value'},
  noDifferences: {id: 'leads.activity.noDifferences', defaultMessage: 'There are no differences'},
  recipient: {id: 'leads.activity.recipient', defaultMessage: 'Recipient'},
  system: {id: 'leads.activity.system', defaultMessage: 'System'},
})

const colon = ':'
const space = ' '
const dash = '-'
const emdash = '—'

const humanizeKey = (key: string) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

export const LeadActivityTab: React.FC<ILeadActivityProps> = ({id}) => {
  const intl = useIntl()
  const na = intl.formatMessage({id: 'common.value.notAvailable', defaultMessage: 'N/A'})
  const roleLabel = (role: string) => {
    const m = roleMessages[role as keyof typeof roleMessages]
    return m ? intl.formatMessage(m) : role || intl.formatMessage(aM.recipient)
  }
  const renderValue = (value: any): React.ReactNode => {
    if (value === null || value === undefined) return na
    if (typeof value === 'boolean')
      return value
        ? intl.formatMessage({id: 'common.boolean.yes', defaultMessage: 'Yes'})
        : intl.formatMessage({id: 'common.boolean.no', defaultMessage: 'No'})
    if (typeof value === 'object') return <pre className='mb-0'>{JSON.stringify(value, null, 2)}</pre>
    return String(value)
  }
  const getUserDisplayName = (user: IResLogsLead['leadLogsByLeadId'][number]['user']) => {
    if (!user) return intl.formatMessage(aM.system)
    return `${user.firstName} ${user.lastName}`.trim() || intl.formatMessage(aM.system)
  }
  const {data, loading, error} = useQuery<IResLogsLead>(GET_LEAD_LOGS, {
    variables: {leadId: Number(id)},
    fetchPolicy: 'no-cache',
  })

  useEffect(() => {
    if (error) toast.error(intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message: error.message}))
  }, [error])

  if (loading || !data) {
    return (
      <div className='d-flex flex-center' style={{height: '20vh'}}>
        <Spinner animation='border' />
      </div>
    )
  }

  if (data.leadLogsByLeadId.length === 0) {
    return (
      <div className='d-flex flex-center' style={{height: '20vh'}}>
        <h4 className='text-muted'>{intl.formatMessage(aM.empty)}</h4>
      </div>
    )
  }

  const logs = [...data.leadLogsByLeadId].reverse()
  const total = logs.length

  return (
    <Accordion defaultActiveKey={`${total}`}>
      {logs.map((log, index) => {
        const {oldValue, newValue, updatedAt, user, changes} = log
        const oldParsed = toDict(oldValue)
        const newParsed = toDict(newValue)
        const number = total - index

        let differences: {field: string; oldValue: any; newValue: any}[] = []

        if (!isPlainMessage(oldParsed, newParsed)) {
          const oldObj = typeof oldParsed === 'object' && oldParsed ? (oldParsed as Record<string, any>) : {}
          const newObj = typeof newParsed === 'object' && newParsed ? (newParsed as Record<string, any>) : {}
          const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)])
          allKeys.forEach((key) => {
            if (key === 'updated') return
            const prev = oldObj[key]
            const next = newObj[key]
            if (JSON.stringify(prev) !== JSON.stringify(next)) {
              differences.push({field: key, oldValue: prev, newValue: next})
            }
          })
        }

        return (
          <Accordion.Item eventKey={`${number}`} key={log.id} className='log-entry'>
            <Accordion.Header className='fw-bold'>
              <span>{number}{colon}</span>&nbsp;
              <span>{moment(updatedAt).format('YYYY-MM-DD HH:mm:ss')}</span>&nbsp;{dash}&nbsp;
              <span className='fw-bold'>{getUserDisplayName(user)}</span>
            </Accordion.Header>
            <Accordion.Body>
              <p>
                <strong>{intl.formatMessage(aM.changes)}{colon}</strong>{space}
                {Array.isArray(changes) && changes.length > 0 ? changes.join(', ') : emdash}
              </p>
              {isNotificationLog(newParsed) ? (
                newParsed.notified.length > 0 ? (
                  <ul className='list-unstyled mb-0'>
                    {newParsed.notified.map((notified: any, i: number) => (
                      <li key={i} className='mb-1'>
                        <span className='fw-bold'>{notified.user_name}</span>{space}
                        <span className='badge bg-light text-dark border'>
                          {roleLabel(notified.role)}
                        </span>{space}
                        {emdash} {intl.formatMessage(aM.email)}{colon}{space}
                        {notified.email_sent ? (
                          <span className='text-success'>{intl.formatMessage(aM.sent)}</span>
                        ) : (
                          <span className='text-warning'>{intl.formatMessage(aM.queued)}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className='text-muted'>{intl.formatMessage(aM.noRecipients)}</div>
                )
              ) : isPlainMessage(oldParsed, newParsed) ? (
                <div className='text-muted'>{intl.formatMessage(aM.noStructured)}</div>
              ) : (
                <table className='table table-striped'>
                  <thead>
                    <tr>
                      <th className='fw-bold'>{intl.formatMessage(aM.field)}</th>
                      <th className='fw-bold'>{intl.formatMessage(aM.previousValue)}</th>
                      <th className='fw-bold'>{intl.formatMessage(aM.newValue)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {differences.length > 0 ? (
                      differences.map(({field, oldValue, newValue}, i) => (
                        <tr key={i}>
                          <td>{humanizeKey(field)}</td>
                          <td>{renderValue(oldValue)}</td>
                          <td>{renderValue(newValue)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3}>{intl.formatMessage(aM.noDifferences)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </Accordion.Body>
          </Accordion.Item>
        )
      })}
    </Accordion>
  )
}
